import type { GelanggangDetail, NoticeItem } from '../types/index';
import { parseGelanggangClasses } from './pricing';

export interface ScheduledActivity {
  id: string;
  title: string;
  type: 'latihan' | 'acara' | 'notis' | 'memanah';
  startTime: Date;
  endTime: Date;
  isLive: boolean; // now >= startTime && now <= endTime
  isToday: boolean;
  dayRelative: string; // e.g. "Hari Ini", "Esok", "Jumaat"
  dateLabel: string; // e.g. "Hari Ini, 8 Sep" or "Jumaat, 11 Sep"
  timeRange: string; // e.g. "8.00 AM - 10.00 AM"
  location?: string;
  leaderOrAuthor?: string;
  className?: string;
}

export const MALAY_DAYS = [
  { name: 'ahad', dayIndex: 0, label: 'Ahad' },
  { name: 'isnin', dayIndex: 1, label: 'Isnin' },
  { name: 'selasa', dayIndex: 2, label: 'Selasa' },
  { name: 'rabu', dayIndex: 3, label: 'Rabu' },
  { name: 'khamis', dayIndex: 4, label: 'Khamis' },
  { name: 'jumaat', dayIndex: 5, label: 'Jumaat' },
  { name: 'sabtu', dayIndex: 6, label: 'Sabtu' },
];

export const SHORT_MONTHS = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogo', 'Sep', 'Okt', 'Nov', 'Dis'];

/**
 * Parses start and end time from strings like:
 * - "(8.00 AM)" -> 8:00 to 10:00
 * - "Jumaat & Sabtu (8.30 PM)" -> 20:30 to 22:30
 * - "Rabu & Sabtu (9.00 PM)" -> 21:00 to 23:00
 * - "8.00 AM - 10.30 AM" -> 8:00 to 10:30
 * - "9.00 Malam" -> 21:00 to 23:00
 */
export function parseStartAndEndTime(rawTime?: string): {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  formattedRange: string;
} {
  let startHour = 8;
  let startMinute = 0;
  let endHour = 10;
  let endMinute = 0;

  if (!rawTime) {
    return { startHour, startMinute, endHour, endMinute, formattedRange: '8.00 AM - 10.00 AM' };
  }

  // Extract content inside parenthesis if exists
  const parenMatch = rawTime.match(/\(([^)]+)\)/);
  const target = (parenMatch ? parenMatch[1] : rawTime).trim();

  // Pattern 1: Range like "8.00 AM - 10.00 AM" or "8:30 PM - 10:30 PM"
  const rangeRegex = /(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?\s*[-–—to]+\s*(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?/i;
  const rangeMatch = target.match(rangeRegex);

  if (rangeMatch) {
    let h1 = parseInt(rangeMatch[1], 10);
    const m1 = rangeMatch[2] ? parseInt(rangeMatch[2], 10) : 0;
    const p1 = rangeMatch[3] ? rangeMatch[3].toUpperCase() : '';

    let h2 = parseInt(rangeMatch[4], 10);
    const m2 = rangeMatch[5] ? parseInt(rangeMatch[5], 10) : 0;
    const p2 = rangeMatch[6] ? rangeMatch[6].toUpperCase() : p1;

    if ((p2 === 'PM' || /malam|petang/i.test(p2)) && h2 < 12) h2 += 12;
    if (p2 === 'AM' && h2 === 12) h2 = 0;

    const p1Final = p1 || p2;
    if ((p1Final === 'PM' || /malam|petang/i.test(p1Final)) && h1 < 12) h1 += 12;
    if (p1Final === 'AM' && h1 === 12) h1 = 0;

    startHour = h1;
    startMinute = m1;
    endHour = h2;
    endMinute = m2;

    const fStart = formatTime12(startHour, startMinute);
    const fEnd = formatTime12(endHour, endMinute);
    return { startHour, startMinute, endHour, endMinute, formattedRange: `${fStart} - ${fEnd}` };
  }

  // Pattern 2: Single time e.g. "8.00 AM" or "8.30 PM" or "9.00 Malam"
  const singleRegex = /(\d{1,2})(?:[.:](\d{2}))?\s*(AM|PM|pagi|malam|petang)?/i;
  const singleMatch = target.match(singleRegex);

  if (singleMatch) {
    let h = parseInt(singleMatch[1], 10);
    const m = singleMatch[2] ? parseInt(singleMatch[2], 10) : 0;
    const period = singleMatch[3] ? singleMatch[3].toUpperCase() : '';

    if ((period === 'PM' || /malam|petang/i.test(period)) && h < 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;

    startHour = h;
    startMinute = m;

    // Default duration is 2 hours (e.g. 8am to 10am, or 8:30pm to 10:30pm)
    endHour = (h + 2) % 24;
    endMinute = m;

    const fStart = formatTime12(startHour, startMinute);
    const fEnd = formatTime12(endHour, endMinute);
    return { startHour, startMinute, endHour, endMinute, formattedRange: `${fStart} - ${fEnd}` };
  }

  return { startHour, startMinute, endHour, endMinute, formattedRange: '8.00 AM - 10.00 AM' };
}

export function formatTime12(hour: number, minute: number): string {
  const period = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 === 0 ? 12 : hour % 12;
  const mStr = minute < 10 ? `0${minute}` : `${minute}`;
  return `${h12}.${mStr} ${period}`;
}

export function parseTrainingDays(sessionTime?: string): number[] {
  if (!sessionTime) return [5, 6];
  const lower = sessionTime.toLowerCase();
  const days: number[] = [];

  MALAY_DAYS.forEach(({ name, dayIndex }) => {
    if (lower.includes(name)) {
      days.push(dayIndex);
    }
  });

  return days.length > 0 ? days : [5, 6];
}

/**
 * Parses day-specific schedules from compound strings like:
 * "Jumaat (8.00 AM - 11.00 AM) & Sabtu (8.00 PM - 10.00 PM)"
 */
export function parseDaySpecificTimes(sessionTime?: string): Record<number, {
  startHour: number;
  startMinute: number;
  endHour: number;
  endMinute: number;
  formattedRange: string;
}> {
  const result: Record<number, {
    startHour: number;
    startMinute: number;
    endHour: number;
    endMinute: number;
    formattedRange: string;
  }> = {};

  if (!sessionTime) return result;

  // Split on & or comma or semicolon
  const parts = sessionTime.split(/[&,;]+/);
  parts.forEach(part => {
    const pTrim = part.trim();
    if (!pTrim) return;
    const pLower = pTrim.toLowerCase();

    MALAY_DAYS.forEach(({ name, dayIndex }) => {
      if (pLower.includes(name)) {
        const timeParsed = parseStartAndEndTime(pTrim);
        result[dayIndex] = timeParsed;
      }
    });
  });

  return result;
}

/**
 * Returns the next upcoming activities scheduled for a given gelanggang.
 *
 * CRITICAL RULE:
 * An activity remains the active display until the current time has passed
 * its END TIME (`now < endTime`).
 *
 * Supports returning both/multiple upcoming sessions (e.g. Jumaat & Sabtu, or Memanah & Silat).
 */
export function getNextScheduledActivities(
  gelanggang: GelanggangDetail,
  notices: NoticeItem[] = [],
  currentTime: Date = new Date(),
  maxCount: number = 2
): ScheduledActivity[] {
  const now = currentTime;
  const classes = parseGelanggangClasses(gelanggang.name, gelanggang.classes);

  if (classes && classes.length > 0) {
    // For gelanggangs with configured classes, find the next upcoming session for EACH configured class
    const classActivities: ScheduledActivity[] = [];

    classes.forEach((c) => {
      const isMemanah = c.name.toLowerCase().includes('memanah');
      const type: 'latihan' | 'memanah' = isMemanah ? 'memanah' : 'latihan';

      let classDays: number[] = [];
      if (c.day && c.day !== 'Semua Hari Sesi' && c.day !== 'Ikut Sesi Gelanggang') {
        const lowerDay = c.day.toLowerCase();
        MALAY_DAYS.forEach(({ name, dayIndex }) => {
          if (lowerDay.includes(name)) classDays.push(dayIndex);
        });
      }
      if (classDays.length === 0) {
        classDays = parseTrainingDays(gelanggang.sessionTime);
      }

      let sH = 8, sM = 0, eH = 10, eM = 0, fRange = '8.00 AM - 10.00 AM';
      if (c.startTime && c.endTime) {
        const [h1, m1] = c.startTime.split(':').map(Number);
        const [h2, m2] = c.endTime.split(':').map(Number);
        sH = h1 || 0;
        sM = m1 || 0;
        eH = h2 || 0;
        eM = m2 || 0;
        fRange = `${formatTime12(sH, sM)} - ${formatTime12(eH, eM)}`;
      } else if (c.schedule) {
        const parsed = parseStartAndEndTime(c.schedule);
        sH = parsed.startHour;
        sM = parsed.startMinute;
        eH = parsed.endHour;
        eM = parsed.endMinute;
        fRange = parsed.formattedRange;
      } else {
        const parsed = parseStartAndEndTime(gelanggang.sessionTime);
        sH = parsed.startHour;
        sM = parsed.startMinute;
        eH = parsed.endHour;
        eM = parsed.endMinute;
        fRange = parsed.formattedRange;
      }

      const title = c.name.startsWith('Latihan') ? c.name : `Latihan ${c.name}`;

      // Search up to 14 days ahead for the next session of this class
      for (let offset = -1; offset <= 14; offset++) {
        const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
        const dayOfWeek = d.getDay();

        if (classDays.includes(dayOfWeek)) {
          const startTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), sH, sM, 0, 0);
          const endTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), eH, eM, 0, 0);

          if (eH < sH) {
            endTime.setDate(endTime.getDate() + 1);
          }

          if (now <= endTime) {
            const isLive = now >= startTime && now <= endTime;
            const isToday = offset === 0 || (offset === -1 && isLive);
            const dayMalay = MALAY_DAYS.find((m) => m.dayIndex === dayOfWeek)?.label || '';
            const dayRelative = isToday ? 'Hari Ini' : offset === 1 ? 'Esok' : dayMalay;
            const dateLabel = isToday
              ? `Hari Ini, ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`
              : offset === 1
              ? `Esok (${dayMalay}), ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`
              : `${dayMalay}, ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;

            classActivities.push({
              id: `class-${gelanggang.id}-${c.name.replace(/\s+/g, '-')}-${d.toISOString().slice(0, 10)}-${sH}${sM}`,
              title,
              type,
              startTime,
              endTime,
              isLive,
              isToday,
              dayRelative,
              dateLabel,
              timeRange: fRange,
              location: gelanggang.location,
              leaderOrAuthor: gelanggang.leaderName,
              className: c.name,
            });
            break; // Found next session for this class!
          }
        }
      }
    });

    // Sort by upcoming start time
    classActivities.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
    return classActivities.slice(0, maxCount);
  }

  // Fallback for gelanggangs without classes
  const daySpecific = parseDaySpecificTimes(gelanggang.sessionTime);
  const trainingDays = parseTrainingDays(gelanggang.sessionTime);
  const globalTimeParsed = parseStartAndEndTime(gelanggang.sessionTime);
  const fallbackCandidates: ScheduledActivity[] = [];

  for (let offset = -1; offset <= 14; offset++) {
    const d = new Date(now.getFullYear(), now.getMonth(), now.getDate() + offset);
    const dayOfWeek = d.getDay();

    if (trainingDays.includes(dayOfWeek)) {
      const timeForDay = daySpecific[dayOfWeek] || globalTimeParsed;
      const { startHour, startMinute, endHour, endMinute, formattedRange } = timeForDay;

      const startTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), startHour, startMinute, 0, 0);
      const endTime = new Date(d.getFullYear(), d.getMonth(), d.getDate(), endHour, endMinute, 0, 0);

      if (endHour < startHour) {
        endTime.setDate(endTime.getDate() + 1);
      }

      if (now <= endTime) {
        const isLive = now >= startTime && now <= endTime;
        const isToday = offset === 0 || (offset === -1 && isLive);
        const dayMalay = MALAY_DAYS.find((m) => m.dayIndex === dayOfWeek)?.label || '';
        const dayRelative = isToday ? 'Hari Ini' : offset === 1 ? 'Esok' : dayMalay;
        const dateLabel = isToday
          ? `Hari Ini, ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`
          : offset === 1
          ? `Esok (${dayMalay}), ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`
          : `${dayMalay}, ${d.getDate()} ${SHORT_MONTHS[d.getMonth()]}`;

        fallbackCandidates.push({
          id: `gelanggang-${gelanggang.id}-${d.toISOString().slice(0, 10)}-${startHour}${startMinute}`,
          title: 'Sesi Latihan Gelanggang',
          type: 'latihan',
          startTime,
          endTime,
          isLive,
          isToday,
          dayRelative,
          dateLabel,
          timeRange: formattedRange,
          location: gelanggang.location,
          leaderOrAuthor: gelanggang.leaderName,
        });

        if (fallbackCandidates.length >= maxCount) break;
      }
    }
  }

  return fallbackCandidates.slice(0, maxCount);
}

export function getNextScheduledActivity(
  gelanggang: GelanggangDetail,
  notices: NoticeItem[] = [],
  currentTime: Date = new Date()
): ScheduledActivity | null {
  const activities = getNextScheduledActivities(gelanggang, notices, currentTime, 1);
  return activities.length > 0 ? activities[0] : null;
}
